import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, CircularProgress } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import { SET_CAMERA_SOURCE } from 'store/actions';
// import Tooltip from '@mui/material/Tooltip';
import { Box } from 'lucide-react';

const EvidenceList = ({ date, device }) => {
  const dispatch = useDispatch();
  const [thumbnails, setThumbnails] = useState({});
  const [videoData, setVideoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('');
  const [visibleItems, setVisibleItems] = useState(5);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const videoRefs = useRef({});
  const thumbnailQueue = useRef([]);
  const processingThumbnail = useRef(false);
  const currentDate = useSelector((state) => state.customization.currentDate);
  const selectedCamera = useSelector((state) => state.cameraReducer.cameraData);
  const loadingRef = useRef(false);
  const thumbnailsProcessedRef = useRef({});
  // const [selectedVideo, setSelectedVideo] = useState(null);

  // Fetch video data
  const fetchVideoData = async (date, device) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('https://db.neophyte.live/sentinel-api/api/sentinel/evidenceList', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, device })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setVideoData(data);
      thumbnailsProcessedRef.current = {};

      if (data && data.length > 0 && data[0]?.videos) {
        const videoUrl = `${data[0].videos}`;
        setSource(videoUrl);
        // setSelectedVideo(data[0]);
        dispatch({ type: SET_CAMERA_SOURCE, videoSource: videoUrl });
      }
    } catch (error) {
      console.error('Error fetching video data:', error);
      setError(error.message || 'Failed to fetch evidence data');
    } finally {
      setLoading(false);
    }
  };

  // const convertLong = (value) => {
  //   if (value && typeof value === 'object' && 'low' in value && 'high' in value) {
  //     return value.low; // or value.toString() if you need the full value
  //   }
  //   return value;
  // };

  // Process thumbnail queue to avoid too many parallel requests
  const processNextThumbnail = useCallback(() => {
    if (processingThumbnail.current || thumbnailQueue.current.length === 0) return;

    processingThumbnail.current = true;
    const { videoPath, index } = thumbnailQueue.current.shift();

    if (thumbnails[index] || thumbnailsProcessedRef.current[index]) {
      processingThumbnail.current = false;
      processNextThumbnail();
      return;
    }

    thumbnailsProcessedRef.current[index] = true;

    // Create a new video element for thumbnail generation
    const video = document.createElement('video');
    videoRefs.current[index] = video;

    // Set important attributes
    video.src = convertAzureBlobLink(videoPath);
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true; // Important for autoplay in some browsers
    video.playsInline = true; // Better mobile support

    // Add to DOM temporarily (can help with CORS issues in some browsers)
    video.style.display = 'none';
    document.body.appendChild(video);

    const cleanup = () => {
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);

      // Proper cleanup
      video.pause();
      video.removeAttribute('src');
      video.load(); // Causes the browser to release resources

      if (document.body.contains(video)) {
        document.body.removeChild(video);
      }

      delete videoRefs.current[index];
      processingThumbnail.current = false;
      setTimeout(processNextThumbnail, 100);
    };

    const onLoadedData = () => {
      // Alternative event if loadedmetadata doesn't fire
      if (!video.seeking) {
        video.currentTime = Math.min(2, video.duration * 0.1);
      }
    };

    const onLoadedMetadata = () => {
      // Video metadata loaded, seek to desired position
      try {
        if (video.duration && video.duration !== Infinity) {
          video.currentTime = Math.min(2, video.duration * 0.1);
        } else {
          // If duration is not available, try to seek to a reasonable position
          video.currentTime = 2;
        }
      } catch (err) {
        console.warn('Error seeking video:', err);
        // Try to play a bit to get frames
        video
          .play()
          .then(() => {
            setTimeout(() => {
              video.pause();
              captureFrame();
            }, 500);
          })
          .catch(cleanup);
      }
    };

    const captureFrame = () => {
      try {
        const canvas = document.createElement('canvas');
        [canvas.width, canvas.height] = [240, 135];
        const ctx = canvas.getContext('2d');

        // Clear canvas with black background
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw video frame
        if (video.videoWidth && video.videoHeight) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailData = canvas.toDataURL('image/jpeg', 0.7);

          // Update thumbnails state
          setThumbnails((prev) => {
            if (prev[index]) return prev;
            return { ...prev, [index]: thumbnailData };
          });
        } else {
          throw new Error('Video dimensions not available');
        }
      } catch (e) {
        console.error(`Thumbnail error for index ${index}:`, e);

        // Generate fallback thumbnail with type info
        generateFallbackThumbnail(index, videoData[index]?.type || 'Video');
      } finally {
        cleanup();
      }
    };

    const onSeeked = () => {
      // Video has been seeked to the position
      captureFrame();
    };

    const onError = (e) => {
      console.error(`Video load error for index ${index}:`, e);

      // Generate fallback thumbnail with type info
      generateFallbackThumbnail(index, videoData[index]?.type || 'Video');

      cleanup();
    };

    // Generate a fallback thumbnail with text
    const generateFallbackThumbnail = (idx, videoType) => {
      const canvas = document.createElement('canvas');
      [canvas.width, canvas.height] = [240, 135];
      const ctx = canvas.getContext('2d');

      // Create gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#2c3e50');
      gradient.addColorStop(1, '#34495e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add text
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(videoType, canvas.width / 2, canvas.height / 2);

      // Add camera icon
      ctx.font = '24px Arial';
      ctx.fillText('🎥', canvas.width / 2, canvas.height / 2 - 30);

      const thumbnailData = canvas.toDataURL('image/jpeg', 0.9);

      setThumbnails((prev) => {
        if (prev[idx]) return prev;
        return { ...prev, [idx]: thumbnailData };
      });
    };

    // Set up event listeners
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    // Start loading the video
    video.load();

    // Try to play (helps with some browser quirks)
    video.play().catch(() => {
      // Ignore play errors - we just want to load the video
    });

    // Set timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      console.warn(`Thumbnail timeout for index ${index}`);

      // Generate fallback thumbnail with type info
      generateFallbackThumbnail(index, videoData[index]?.type || 'Video');

      cleanup();
    }, 10000);

    video.timeoutId = timeoutId;
  }, [thumbnails, videoData]);

  // Add thumbnail to processing queue
  const queueThumbnail = useCallback(
    (videoPath, index) => {
      if (!videoPath || thumbnails[index] || thumbnailsProcessedRef.current[index]) return;

      thumbnailQueue.current.push({ videoPath, index });

      if (!processingThumbnail.current) {
        processNextThumbnail();
      }
    },
    [thumbnails, processNextThumbnail]
  );

  // Memoize video data to prevent unnecessary re-renders
  const memoizedVideoData = useMemo(() => videoData, [JSON.stringify(videoData)]);

  // Intersection Observer setup for lazy loading
  useEffect(() => {
    if (!memoizedVideoData?.length) return;

    // Don't clear queue when component re-renders to prevent blinking
    // Only add new items to the queue

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            const videoPath = memoizedVideoData[index]?.videos;
            // Only queue if we don't have this thumbnail already and it's not being processed
            if (videoPath && !thumbnails[index] && !thumbnailsProcessedRef.current[index]) {
              queueThumbnail(videoPath, index);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observerRef.current = observer;

    // Observe initial items
    document.querySelectorAll('[data-video-item]').forEach((item) => {
      const index = parseInt(item.dataset.index, 10);
      if (index < visibleItems) observer.observe(item);
    });

    return () => {
      observer.disconnect();
      Object.values(videoRefs.current).forEach((video) => {
        if (video && video.timeoutId) {
          clearTimeout(video.timeoutId);
        }
        if (video) {
          video.pause();
          video.removeAttribute('src');
          video.load();
          if (document.body.contains(video)) {
            document.body.removeChild(video);
          }
        }
      });
    };
  }, [memoizedVideoData, queueThumbnail, visibleItems, thumbnails]);

  useEffect(() => {
    if (!observerRef.current || !memoizedVideoData?.length) return;

    const newItems = Array.from({ length: 5 }, (_, i) => visibleItems - 5 + i)
      .filter((i) => i >= 0 && i < memoizedVideoData.length)
      .map((i) => document.querySelector(`[data-video-item][data-index="${i}"]`))
      .filter(Boolean);

    newItems.forEach((item) => observerRef.current.observe(item));
  }, [visibleItems, memoizedVideoData]);

  // Load more items on scroll
  const loadMoreItems = useCallback(() => {
    if (loadingRef.current || visibleItems >= memoizedVideoData.length) return;

    loadingRef.current = true;
    setVisibleItems((prev) => {
      const newValue = Math.min(prev + 5, memoizedVideoData.length);
      loadingRef.current = false;
      return newValue;
    });
  }, [visibleItems, memoizedVideoData.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY > document.body.offsetHeight - 800) {
        loadMoreItems();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // This cleanup only runs on complete component unmount
      thumbnailQueue.current = [];
      processingThumbnail.current = false;
      thumbnailsProcessedRef.current = {};

      // Clean up any pending video elements
      Object.values(videoRefs.current).forEach((video) => {
        if (video) {
          if (video.timeoutId) clearTimeout(video.timeoutId);
          video.src = '';
          video.remove();
        }
      });
      videoRefs.current = {};
    };
  }, []);

  // Fetch data when params change
  useEffect(() => {
    const dateToUse = date || currentDate;
    const deviceToUse = device?.device_name || selectedCamera?.device_name;
    if (dateToUse && deviceToUse) fetchVideoData(dateToUse, deviceToUse);
  }, [date, device, currentDate, selectedCamera]);

  // const getTypeColor = (type) => {
  //   switch (type?.toLowerCase()) {
  //     case 'alert':
  //       return 'bg-red-500';
  //     case 'motion':
  //       return 'bg-orange-500';
  //     case 'person':
  //       return 'bg-purple-500';
  //     case 'vehicle':
  //       return 'bg-blue-500';
  //     case 'dock':
  //       return 'bg-green-500';
  //     default:
  //       return 'bg-gray-500';
  //   }
  // };

  // const formatTimestamp = (timestamp) => {
  //   if (!timestamp) return '';
  //   try {
  //     const date = new Date(timestamp);
  //     return date.toLocaleTimeString();
  //   } catch (e) {
  //     const matches = timestamp.match(/(\d{2}):(\d{2}):(\d{2})/);
  //     if (matches) {
  //       return matches[0];
  //     }
  //     return timestamp;
  //   }
  // };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-64">
        <CircularProgress />
      </Box>
    );
  }

  if (error)
    return (
      <Alert severity="error" className="my-4">
        {error}
      </Alert>
    );

  if (!memoizedVideoData?.length)
    return (
      <Alert severity="info" className="my-4">
        No evidence found for this date and device.
      </Alert>
    );

  return (
    <div ref={containerRef}>
      {/* <Divider className="my-4" /> */}
      <div className="mb-4">
        {/* <Typography variant="h6" className="mb-3">
          All Evidence ({memoizedVideoData.length})
        </Typography> */}

        <div className="mb-0 flex flex-col space-y-4">
          {memoizedVideoData.slice(0, visibleItems).map((videoGroup, index) => (
            <div key={`video-${index}`} data-video-item data-index={index}>
              <div
                className={`flex bg-white h-32 rounded-lg shadow-lg text-xs p-4 relative hover:shadow-xl transition-all ${
                  source.endsWith(videoGroup?.videos) ? 'border-2 border-blue-500 bg-blue-50' : ''
                } ${!videoGroup?.videos ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                onClick={() => {
                  if (!videoGroup?.videos) return;

                  const videoUrl = videoGroup.videos;
                  dispatch({ type: SET_CAMERA_SOURCE, videoSource: videoUrl });
                  setSource(videoUrl);
                  // setSelectedVideo(videoGroup);
                }}
              >
                {/* Left Side: Video Thumbnail */}
                <div className="flex w-1/3 items-center justify-center bg-gray-200 rounded-lg overflow-hidden">
                  {thumbnails[index] ? (
                    <img
                      src={thumbnails[index]}
                      alt={`${videoGroup?.type || 'Video'} Thumbnail`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <VideocamIcon className="text-4xl" />
                    </div>
                  )}
                </div>

                {/* Right Side: Video Info */}
                <div className="flex flex-col flex-grow px-4">
                  {/* <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-lg font-semibold cursor-default hover:text-blue-600 transition-colors duration-200">
                      {videoGroup?.type?.charAt(0)?.toUpperCase() + videoGroup?.type?.slice(1) || 'Video'}
                    </h2>
                  </div> */}

                  {/* <div className="flex flex-wrap gap-2 mb-2">
                    {videoGroup.shoes === false && (
                      <Tooltip
                        title={
                          <div className="p-2">
                            <span className="text-gray-800 font-medium text-sm">
                              Track ID {convertLong(videoGroup.track_id) || ''} was not wearing shoes
                            </span>
                          </div>
                        }
                        placement="bottom"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'white',
                              color: 'text.primary',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              padding: '12px',
                              maxWidth: 'none'
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'white',
                              '&:before': {
                                border: '1px solid #e5e7eb'
                              }
                            }
                          }
                        }}
                        enterTouchDelay={0}
                        leaveTouchDelay={2000}
                      >
                        <div
                          className={`text-black border border-gray-300 hover:border-amber-500 hover:text-amber-500 text-sm px-3 py-1 rounded-full font-medium cursor-pointer select-none flex items-center justify-center w-fit text-center transition-colors duration-200`}
                        >
                          <span>Shoes</span>
                        </div>
                      </Tooltip>
                    )}

                    {videoGroup.ppe === false && (
                      <Tooltip
                        title={
                          <div className="p-2">
                            <span className="text-gray-800 font-medium text-sm">
                              Track ID {convertLong(videoGroup.track_id) || ''} was not wearing PPE equipment
                            </span>
                          </div>
                        }
                        placement="bottom"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'white',
                              color: 'text.primary',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              padding: '12px',
                              maxWidth: 'none'
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'white',
                              '&:before': {
                                border: '1px solid #e5e7eb'
                              }
                            }
                          }
                        }}
                        enterTouchDelay={0}
                        leaveTouchDelay={2000}
                      >
                        <div
                          className={`text-black border border-gray-300 hover:border-amber-500 hover:text-amber-500 text-sm px-3 py-1 rounded-full font-medium cursor-pointer select-none flex items-center justify-center w-fit text-center transition-colors duration-200`}
                        >
                          <span>PPE</span>
                        </div>
                      </Tooltip>
                    )}

                    {videoGroup.helmet === false && (
                      <Tooltip
                        title={
                          <div className="p-2">
                            <span className="text-gray-800 font-medium text-sm">
                              Track ID {convertLong(videoGroup.track_id) || ''} was not wearing a helmet
                            </span>
                          </div>
                        }
                        placement="bottom"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'white',
                              color: 'text.primary',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              padding: '12px',
                              maxWidth: 'none'
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'white',
                              '&:before': {
                                border: '1px solid #e5e7eb'
                              }
                            }
                          }
                        }}
                        enterTouchDelay={0}
                        leaveTouchDelay={2000}
                      >
                        <div
                          className={`text-black border border-gray-300 hover:border-amber-500 hover:text-amber-500 text-sm px-3 py-1 rounded-full font-medium cursor-pointer select-none flex items-center justify-center w-fit text-center transition-colors duration-200`}
                        >
                          <span>Helmet</span>
                        </div>
                      </Tooltip>
                    )}

                    {videoGroup?.device && videoGroup.device === 'D014' && (
                      <Tooltip
                        title={
                          <div className="p-2">
                            <span className="text-gray-800 font-medium text-sm">
                              Exit camera view - capturing individuals leaving the area
                            </span>
                          </div>
                        }
                        placement="bottom"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'white',
                              color: 'text.primary',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              padding: '12px',
                              maxWidth: 'none'
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'white',
                              '&:before': {
                                border: '1px solid #e5e7eb'
                              }
                            }
                          }
                        }}
                        enterTouchDelay={0}
                        leaveTouchDelay={2000}
                      >
                        <div
                          className={`text-black border border-gray-300 hover:border-blue-500 hover:text-blue-500 text-sm px-3 py-1 rounded-full font-medium cursor-pointer select-none flex items-center justify-center w-fit text-center transition-colors duration-200`}
                        >
                          <span>Exit</span>
                        </div>
                      </Tooltip>
                    )}

                    {videoGroup?.device && videoGroup.device === 'D012' && (
                      <Tooltip
                        title={
                          <div className="p-2">
                            <span className="text-gray-800 font-medium text-sm">
                              Entry camera view - capturing individuals entering the area
                            </span>
                          </div>
                        }
                        placement="bottom"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'white',
                              color: 'text.primary',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              padding: '12px',
                              maxWidth: 'none'
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'white',
                              '&:before': {
                                border: '1px solid #e5e7eb'
                              }
                            }
                          }
                        }}
                        enterTouchDelay={0}
                        leaveTouchDelay={2000}
                      >
                        <div
                          className={`text-black border border-gray-300 hover:border-blue-500 hover:text-blue-500 text-sm px-3 py-1 rounded-full font-medium cursor-pointer select-none flex items-center justify-center w-fit text-center transition-colors duration-200`}
                        >
                          <span>Entry</span>
                        </div>
                      </Tooltip>
                    )}
                  </div> */}
                </div>
              </div>
            </div>
          ))}
        </div>

        {visibleItems < memoizedVideoData.length && (
          <div className="flex justify-center mt-4">
            <button onClick={loadMoreItems} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidenceList;
